import { useState } from "react";
import { useInputPopup } from "../../components/InputPopup/index";
import { useOnline } from "../../core/hooks/useOnline";
import { Trans, useTranslation } from "@coworker/locales";
import activityTypes from "@coworker/enums/activityTypes";
import taskStateOptions from "@coworker/enums/taskState";
import { dropTypeKeys, dropTypeOptions } from "@coworker/enums/dropTypes";
import { closeTypeKeys, closeTypeOptions } from "@coworker/enums/closeTypes";
import { useToastNotification } from "@coworker/components/src/components/ToastNotification";
import trackerHelper from "../../helpers/tracker";
import Time from "../../assets/svg/time.svg?react";
import Issue from "../../assets/svg/issue.svg?react";
import IssueOther from "@coworker/reusable/svg/three-dots.svg?react";
import InfoCircle from "../../assets/svg/information-circle.svg?react";
import Incorrect from "../../assets/svg/incorrect.svg?react";
import AlreadyResolved from "../../assets/svg/test-buy.svg?react";
import NotInStock from "../../assets/svg/not-in-stock.svg?react";
import { useUID, useTeamId } from "../../core/auth/useLoggedInUser";
import { useQueryClient } from "@tanstack/react-query";
import { useUpdateTaskMutation } from "@coworker/apprestructured/src/tasks/hooks/useUpdateTaskById";
import { useUpdateTask } from "@coworker/apprestructured/src/task-management/services/useUpdateTask";
import { useBulkUpdateTasks } from "@coworker/apprestructured/src/task-management/services/useBulkUpdateTasks";
import { invalidateTaskQueries } from "@coworker/apprestructured/src/task-management/services/queryKeyHelper";
import { useActiveStoreUsers } from "@coworker/apprestructured/src/shared/hooks/users/useActiveStoreUsers";

const {
  CLOSE,
  COMPLETE,
  REASSIGN,
  REASSIGN_TEAM,
  DROP,
  PICK_UP,
  UNDO_CLOSE,
  UNDO_COMPLETE,
} = activityTypes;

function mapCloseReasonToV2(v1CloseType) {
  switch (v1CloseType) {
    case closeTypeOptions.INCORRECT:
      return "invalid";
    case closeTypeOptions.ALREADY_RESOLVED:
      return "already_resolved";
    case closeTypeOptions.NOT_IN_STOCK:
      return "other";
    case closeTypeOptions.OTHER:
      return "other";
    default:
      return "other";
  }
}

export function useMultiselectOverflowMenu(
  count,
  type,
  v1TaskIds,
  v2TaskIds,
  closeReasonOptions = [],
  resetSelectedTasks
) {
  const { getInput, closePopup } = useInputPopup();
  const { showToast } = useToastNotification();

  const uid = useUID();
  const teamId = useTeamId();
  const { isOnline } = useOnline();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const updateTaskV1 = useUpdateTaskMutation();
  const updateTaskV2 = useUpdateTask();
  const bulkUpdateTasksV2 = useBulkUpdateTasks();

  const { data: activeUsers } = useActiveStoreUsers();

  const makeBaseFields = (
    activity_type,
    assigned_user_id,
    assigned_team_id
  ) => ({
    activity_type,
    assigned_user_id,
    assigned_team_id,
    last_editor_id: uid,
    state:
      activity_type === CLOSE
        ? taskStateOptions.CLOSED
        : activity_type === COMPLETE
          ? taskStateOptions.COMPLETED
          : assigned_user_id
            ? taskStateOptions.ASSIGNED
            : taskStateOptions.UNASSIGNED,
  });

  const makeTaskChanger = (taskOption, data) => {
    switch (taskOption) {
      case DROP:
        return () => ({
          ...makeBaseFields(DROP, "", teamId),
          drop_type: data.issue,
          drop_description: data.description,
        });
      case PICK_UP:
        return () => makeBaseFields(PICK_UP, uid, teamId);
      case REASSIGN: {
        const reassignTeamOrUser =
          data.gid && data.uid ? REASSIGN : REASSIGN_TEAM;
        return () => makeBaseFields(reassignTeamOrUser, data.uid, data.gid);
      }
      case CLOSE:
        return () => ({
          ...makeBaseFields(CLOSE, uid, teamId),
          close_type: data.issue,
          close_description: data.description,
        });
      case COMPLETE:
        return () => makeBaseFields(COMPLETE, uid, teamId);
      default:
    }
  };

  const showResults = (failedTasksCount) => {
    if (failedTasksCount && failedTasksCount <= count) {
      showToast(
        t("nTasksNotChangedString", {
          number: count - failedTasksCount,
          count: count,
        }),
        <InfoCircle />,
        () => getInput("multiSelectPopup")
      );
    } else {
      showToast(<Trans count={count}>nTasksChangedString</Trans>);
    }
  };

  const processTasks = async (taskOption, activity) => {
    const promises = [];

    try {
      if (v1TaskIds.length > 0) {
        const taskChanger = makeTaskChanger(taskOption, activity);
        const changes = taskChanger();

        const v1Promises = v1TaskIds.map((taskId) =>
          updateTaskV1.mutateAsync({ data: changes, taskId })
        );
        promises.push(...v1Promises);
      }

      if (v2TaskIds.length > 0) {
        let v2UpdateDTO;

        switch (taskOption) {
          case DROP:
            v2UpdateDTO = { assigned_user_id: null };
            break;
          case COMPLETE:
            v2UpdateDTO = { state: "completed" };
            break;
          case PICK_UP:
            v2UpdateDTO = { assigned_user_id: uid };
            break;
          case REASSIGN:
            if (activity.gid && activity.uid) {
              v2UpdateDTO = {
                assigned_user_id: activity.uid,
                assigned_team_id: activeUsers.find(
                  (user) => user.uid === activity.uid
                ).teamId,
              };
            } else if (activity.gid) {
              v2UpdateDTO = {
                assigned_user_id: null,
                assigned_team_id: activity.gid,
              };
            }
            break;
          case CLOSE: {
            const closeReasonId =
              activity.closeReasonId ?? mapCloseReasonToV2(activity.issue);

            const closeDescription =
              activity.description ||
              (!activity.closeReasonId && closeReasonId === "other"
                ? t("noDescriptionString")
                : "");

            v2UpdateDTO = {
              state: "closed",
              close_reason_id: closeReasonId,
              close_description: closeDescription,
            };
            break;
          }
          default:
            v2UpdateDTO = null;
        }

        if (v2UpdateDTO) {
          const v2Promises = v2TaskIds.map((taskId) =>
            updateTaskV2.mutateAsync({
              id: taskId,
              patchTask: v2UpdateDTO,
            })
          );
          promises.push(...v2Promises);
        }
      }

      const results = await Promise.allSettled(promises);
      const failedTasksCount = results.filter(
        (result) => result.status === "rejected"
      ).length;

      trackerHelper.multipleSelect.logProcessed(
        count,
        failedTasksCount,
        taskOption
      );

      await invalidateTaskQueries(queryClient);

      if (failedTasksCount > 0) {
        showResults(failedTasksCount);
      } else {
        showResults(0);
      }

      if (resetSelectedTasks) {
        resetSelectedTasks();
      }
    } catch (error) {
      console.error("Error applying changes:", error);
    } finally {
      setLoading(false);
    }
  };

  const completeTask = {
    isDisabled: !isOnline,
    onClick: async () => {
      setLoading(true);
      closePopup();
      await processTasks(COMPLETE, uid);
    },
    title: <Trans>completeTasksString</Trans>,
    enum: COMPLETE,
  };

  const dropTask = {
    isDisabled: !isOnline,
    onClick: async () => {
      const dropTaskReason = await getInput("taskAction", {
        customTitle: <Trans>whyAreYouDroppingString</Trans>,
        content: [
          {
            icon: Time,
            name: <Trans>{dropTypeKeys.END_OF_SHIFT}</Trans>,
            type: dropTypeOptions.END_OF_SHIFT,
          },
          {
            icon: Issue,
            name: <Trans>{dropTypeKeys.ISSUE}</Trans>,
            type: dropTypeOptions.ISSUE,
          },
          {
            icon: IssueOther,
            name: <Trans>{dropTypeKeys.OTHER}</Trans>,
            type: dropTypeOptions.OTHER,
            descriptionRequired: true,
          },
        ],
      });
      if (!dropTaskReason) return;
      setLoading(true);
      await processTasks(DROP, dropTaskReason);
    },
    title: <Trans>dropTasksString</Trans>,
    enum: DROP,
  };

  const pickupTask = {
    isDisabled: !isOnline,
    onClick: async () => {
      setLoading(true);
      await processTasks(PICK_UP, uid);
      closePopup();
    },
    title: <Trans>pickTasksString</Trans>,
    enum: PICK_UP,
  };

  const closeTask = {
    isDisabled:
      !isOnline || (v2TaskIds.length > 0 && closeReasonOptions.length === 0),
    onClick: async () => {
      const hasV2Tasks = v2TaskIds.length > 0;
      const closeTaskReason = await getInput("taskAction", {
        customTitle: <Trans>whyRUClosingString</Trans>,
        content: hasV2Tasks
          ? closeReasonOptions.map((option) => ({
              icon: IssueOther,
              name: option.label,
              type: option.value,
              descriptionRequired: option.requiresDescription,
            }))
          : [
              {
                icon: Incorrect,
                name: <Trans>{closeTypeKeys.INCORRECT}</Trans>,
                type: closeTypeOptions.INCORRECT,
              },
              {
                icon: AlreadyResolved,
                name: <Trans>{closeTypeKeys.ALREADY_RESOLVED}</Trans>,
                type: closeTypeOptions.ALREADY_RESOLVED,
              },
              {
                icon: NotInStock,
                name: <Trans>{closeTypeKeys.NOT_IN_STOCK}</Trans>,
                type: closeTypeOptions.NOT_IN_STOCK,
              },
              {
                icon: IssueOther,
                name: <Trans>{closeTypeKeys.OTHER}</Trans>,
                type: closeTypeOptions.OTHER,
                descriptionRequired: true,
              },
            ],
      });
      if (!closeTaskReason) return;
      setLoading(true);
      await processTasks(CLOSE, {
        ...closeTaskReason,
        closeReasonId: hasV2Tasks ? closeTaskReason.issue : undefined,
        issue: hasV2Tasks ? closeTypeOptions.OTHER : closeTaskReason.issue,
      });
    },
    title: <Trans>closeTasksString</Trans>,
    enum: CLOSE,
  };

  const isCompletedTasks = type === "completed";
  const isClosedTasks = type === "closed";

  const processReopenTasks = async () => {
    try {
      const tasks = v2TaskIds.map((taskId) => ({
        id: taskId,
        patch: {
          state: "pending",
          ...(isClosedTasks
            ? {
                close_reason_id: null,
                close_description: "",
              }
            : {}),
        },
      }));

      if (tasks.length > 0) {
        await bulkUpdateTasksV2.mutateAsync(tasks);
      }

      const failedTasksCount = v1TaskIds.length;

      trackerHelper.multipleSelect.logProcessed(
        count,
        failedTasksCount,
        isClosedTasks ? UNDO_CLOSE : UNDO_COMPLETE
      );

      await invalidateTaskQueries(queryClient);
      showResults(failedTasksCount);

      if (resetSelectedTasks) {
        resetSelectedTasks();
      }
    } catch (error) {
      console.error("Error reopening tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  const reopenTask = {
    isDisabled: !isOnline,
    onClick: async () => {
      setLoading(true);
      closePopup();
      await processReopenTasks();
    },
    title: <Trans>reopenString</Trans>,
    enum: isClosedTasks ? UNDO_CLOSE : UNDO_COMPLETE,
  };

  const isInProgressTasks = type === "inProgress";
  const titleAction = {
    title: (
      <h4>
        <Trans count={count}>applyNTasksString</Trans>
      </h4>
    ),
  };
  const actions =
    isCompletedTasks || isClosedTasks
      ? [titleAction, reopenTask]
      : [
          titleAction,
          isInProgressTasks ? dropTask : pickupTask,
          completeTask,
          {
            isDisabled: !isOnline,
            onClick: async () => {
              const assignTo = await getInput("selectAssignees", {});
              if (!assignTo) return;
              setLoading(true);
              await processTasks(REASSIGN, assignTo);
            },
            title: <Trans>reassignTasksString</Trans>,
            enum: REASSIGN,
          },
          closeTask,
        ];
  const getTaskMenu = () => getInput("popupMenu", { actions });

  return { loading, getTaskMenu };
}
